"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import Link from "next/link";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import HoverCard from "@/components/utils/tooltip/HoverCard";
import PermissionIcon from "@/components/utils/icons/PermissionIcon";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getWavePathRoute } from "@/helpers/navigation.helpers";
import { MEMES_NOMINEE_REQUIRED_REP, MEMES_SEEKING_NOMINATION_WAVE_ID } from "./memesNomination.constants";
import { useMemesNomineeProgress, type NominationProgress } from "./useMemesNomineeProgress";
const SEEKING_NOMINATION_ROUTE = getWavePathRoute(MEMES_SEEKING_NOMINATION_WAVE_ID);
interface MainStageNominationPopoverProps {
  readonly children: ReactElement;
}
function RepStatusCard(t0) {
  const $ = _c(8);
  const {
    children,
    title
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-tracking-wider tw-text-iron-400">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = title ? "tw-mb-0 tw-mt-2 tw-text-xs tw-font-medium tw-leading-5 tw-text-iron-500" : "tw-mb-0 tw-text-xs tw-font-medium tw-text-iron-400";
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <p className={t2}>{children}</p>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <div className="tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-white/5 tw-p-4">{t1}{t3}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function AnimatedProgressBar(t0) {
  const $ = _c(18);
  const {
    label,
    percent,
    valueMax,
    valueNow,
    valueText
  } = t0;
  const [animatedPercent, setAnimatedPercent] = useState(0);
  let t1;
  let t2;
  if ($[0] !== percent) {
    t1 = () => {
      const frameId = globalThis.requestAnimationFrame(() => {
        setAnimatedPercent(percent);
      });
      return () => globalThis.cancelAnimationFrame(frameId);
    };
    t2 = [percent];
    $[0] = percent;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== label || $[4] !== valueMax || $[5] !== valueNow || $[6] !== valueText) {
    t3 = <progress className="tw-sr-only" max={valueMax} value={valueNow} aria-label={label} aria-valuetext={valueText}>{valueText}</progress>;
    $[3] = label;
    $[4] = valueMax;
    $[5] = valueNow;
    $[6] = valueText;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = `scaleX(${animatedPercent / 100})`;
  let t5;
  if ($[8] !== t4) {
    t5 = {
      transform: t4
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== percent) {
    t6 = percent > 0 && <span className="tw-pointer-events-none tw-absolute tw-right-0 tw-top-1/2 tw-h-3 tw-w-3 -tw-translate-y-1/2 tw-translate-x-1/2 tw-rounded-full tw-bg-white/80 tw-blur-[3px]" />;
    $[10] = percent;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div aria-hidden="true" className="tw-relative tw-h-full tw-w-full tw-origin-left tw-transform-gpu tw-overflow-hidden tw-rounded-full tw-bg-gradient-to-r tw-from-primary-600 tw-via-primary-500 tw-to-primary-300 tw-shadow-[0_0_14px_rgba(64,106,254,0.58)] tw-transition-transform tw-duration-1000 tw-ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:tw-transition-none" style={t5}>{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <div className="tw-relative tw-z-10 tw-h-1 tw-w-full tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-white/[0.05] tw-bg-iron-950 tw-shadow-[inset_0_1px_2px_rgba(0,0,0,0.85)]">{t3}{t7}</div>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function RepProgressSection(t0) {
  const $ = _c(23);
  const {
    hasProfile,
    isError,
    isLoading,
    progress
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <RepStatusCard><span className="tw-flex tw-items-center tw-gap-2"><CircleLoader size={CircleLoaderSize.SMALL} /><span>Loading...</span></span></RepStatusCard>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (isError) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <RepStatusCard title="MemesNominee REP">Could not load MemesNominee REP right now.</RepStatusCard>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (!hasProfile || !progress) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <RepStatusCard title="MemesNominee REP">Connect a profile to see your progress toward eligibility.</RepStatusCard>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  const currentRep = Math.floor(progress.currentRep);
  const cappedCurrentRep = Math.min(Math.max(currentRep, 0), MEMES_NOMINEE_REQUIRED_REP);
  let t1;
  if ($[3] !== currentRep) {
    t1 = formatNumberWithCommas(currentRep);
    $[3] = currentRep;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const formattedCurrentRep = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = formatNumberWithCommas(MEMES_NOMINEE_REQUIRED_REP);
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const formattedRequiredRep = t2;
  let t3;
  if ($[6] !== progress.remainingRep) {
    t3 = progress.remainingRep > 0 ? `${formatNumberWithCommas(Math.ceil(progress.remainingRep))} REP to go` : "REP requirement met";
    $[6] = progress.remainingRep;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const progressLabel = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_86%_0%,rgba(64,106,254,0.14),transparent_44%)]" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="tw-text-xs tw-font-semibold tw-tracking-wider tw-text-iron-400">MemesNominee REP</span>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="tw-text-xs tw-font-medium tw-text-iron-600">{" / "}{formattedRequiredRep}</span>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== formattedCurrentRep) {
    t7 = <div className="tw-relative tw-z-10 tw-mb-3 tw-flex tw-items-end tw-justify-between tw-gap-4">{t5}<span className="tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-text-iron-200">{formattedCurrentRep}{t6}</span></div>;
    $[11] = formattedCurrentRep;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = `${formattedCurrentRep} of ${formattedRequiredRep} MemesNominee REP`;
  let t9;
  if ($[13] !== cappedCurrentRep || $[14] !== progress.percent || $[15] !== t8) {
    t9 = <AnimatedProgressBar label="MemesNominee REP progress" percent={progress.percent} valueMax={MEMES_NOMINEE_REQUIRED_REP} valueNow={cappedCurrentRep} valueText={t8} />;
    $[13] = cappedCurrentRep;
    $[14] = progress.percent;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== progressLabel) {
    t10 = <p className="tw-relative tw-z-10 tw-mb-0 tw-mt-2.5 tw-text-right tw-text-xs tw-font-medium tw-text-iron-500">{progressLabel}</p>;
    $[17] = progressLabel;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7 || $[21] !== t9) {
    t11 = <div className="tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-gradient-to-b tw-from-iron-800/65 tw-to-iron-900/80 tw-p-4 tw-shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04),inset_0_1px_0_rgba(255,255,255,0.035),0_8px_18px_rgba(0,0,0,0.18)]">{t4}{t7}{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}
function MainStageNominationPopoverContent() {
  const $ = _c(10);
  const {
    hasProfile,
    isError,
    isLoading,
    progress
  } = useMemesNomineeProgress();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2"><PermissionIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-400" /><p className="tw-mb-0 tw-text-sm tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-iron-100">Unlock submissions</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-mb-5 tw-flex tw-flex-col tw-items-start">{t0}<div className="tw-min-w-0"><p className="tw-mb-0 tw-text-xs tw-font-medium tw-leading-relaxed tw-text-iron-400">Reach {formatNumberWithCommas(MEMES_NOMINEE_REQUIRED_REP)}{" "}MemesNominee REP to become eligible to submit work.</p></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hasProfile || $[3] !== isError || $[4] !== isLoading || $[5] !== progress) {
    t2 = <div className="tw-mb-5"><RepProgressSection hasProfile={hasProfile} isError={isError} isLoading={isLoading} progress={progress} /></div>;
    $[2] = hasProfile;
    $[3] = isError;
    $[4] = isLoading;
    $[5] = progress;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Link href={SEEKING_NOMINATION_ROUTE} className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-x-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-primary-500 tw-bg-primary-500 tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-white tw-no-underline tw-shadow-sm tw-ring-1 tw-ring-primary-500 tw-transition tw-duration-300 tw-ease-out focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-600 desktop-hover:hover:tw-border-primary-600 desktop-hover:hover:tw-bg-primary-600 desktop-hover:hover:tw-text-white desktop-hover:hover:tw-ring-primary-600">Get nominated<ArrowRightIcon className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-opacity-70" /></Link>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2) {
    t4 = <div className="tw-w-[min(88vw,20rem)]">{t1}{t2}{t3}</div>;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export default function MainStageNominationPopover(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <MainStageNominationPopoverContent />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <HoverCard content={t1} ariaLabel="Main Stage submission eligibility" placement="bottom" delayShow={150} delayHide={0} offset={12} openOnClick={true}>{children}</HoverCard>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}